// Microphone dictation of the composer (D36) through the browser's own speech recognition (Web Speech API). It only
// fills the field — it never sends — and reports a visible state; where the API does not exist the button is disabled
// with an explanation. No audio leaves the browser through MaestrOS (the browser's engine handles it).
import { useCallback, useEffect, useRef, useState } from "react";

interface RecognitionResultList {
  length: number;
  [index: number]: { isFinal: boolean; length: number; [index: number]: { transcript: string } };
}
export interface RecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: ((event: { resultIndex: number; results: RecognitionResultList }) => void) | null;
  onerror: ((event: { error?: string }) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}
type RecognitionCtor = new () => RecognitionLike;

export function recognitionConstructor(): RecognitionCtor | null {
  if (typeof window === "undefined") return null;
  const w = window as unknown as { SpeechRecognition?: RecognitionCtor; webkitSpeechRecognition?: RecognitionCtor };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

/** BCP 47 tag for the recognizer from the UI locale ("en" → "en-US"). */
export function recognitionLang(locale: string): string {
  return locale === "en" ? "en-US" : locale;
}

export type DictationState = "idle" | "listening" | "failed";

/** Final transcripts are handed to `onText` (the caller appends them to the field); interim ones are not. */
export function useDictation(locale: string, onText: (text: string) => void) {
  const ctor = recognitionConstructor();
  const [state, setState] = useState<DictationState>("idle");
  const rec = useRef<RecognitionLike | null>(null);
  const onTextRef = useRef(onText);
  onTextRef.current = onText;

  const stop = useCallback(() => {
    rec.current?.stop();
  }, []);

  const start = useCallback(() => {
    if (!ctor || rec.current) return;
    const r = new ctor();
    r.lang = recognitionLang(locale);
    r.continuous = true;
    r.interimResults = false;
    r.onresult = (event) => {
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (result?.isFinal) {
          const text = result[0]?.transcript.trim() ?? "";
          if (text) onTextRef.current(text);
        }
      }
    };
    r.onerror = (event) => {
      // "no-speech" and "aborted" are a normal end; anything else (not-allowed, audio-capture…) is shown.
      if (event.error !== "no-speech" && event.error !== "aborted") setState("failed");
    };
    r.onend = () => {
      rec.current = null;
      setState((s) => (s === "listening" ? "idle" : s));
    };
    rec.current = r;
    setState("listening");
    try {
      r.start();
    } catch {
      rec.current = null;
      setState("failed");
    }
  }, [ctor, locale]);

  useEffect(() => () => rec.current?.abort(), []);

  return { supported: ctor !== null, state, listening: state === "listening", start, stop };
}
